import { useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { TotalUnitsChange } from '../../api/types'
import { formatDateTime, formatUnits } from './format'

/** Kolik posledních pohybů se vypíše v kartě (celá historie je v dialogu). */
const MAX_ROWS = 5

interface TotalUnitsHistoryProps {
  changes: TotalUnitsChange[]
  /** Chyba načtení historie — nesmí shodit celou fázi, hlásí se jen tady. */
  error?: string | null
}

function delta(change: TotalUnitsChange): string {
  const d = change.totalUnitsAfter - change.totalUnitsBefore
  if (Math.abs(d) < 0.00005) return '0'
  return (d > 0 ? '+' : '−') + formatUnits(Math.abs(d))
}

function HistoryTable({ rows, withDelta }: { rows: TotalUnitsChange[]; withDelta?: boolean }) {
  return (
    <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.75 } }} stickyHeader={withDelta}>
      <TableHead>
        <TableRow>
          <TableCell>Kdy</TableCell>
          {withDelta && <TableCell align="right">Změna</TableCell>}
          <TableCell align="right">Jednotky (před → po)</TableCell>
          <TableCell>Kdo</TableCell>
          <TableCell>Pohyb</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((change, index) => (
          // id je z BE, ale kdyby chybělo, ať se řádky nesloučí pod stejný klíč
          <TableRow key={change.id || `${change.changedAt}#${index}`}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              {change.changedAt ? formatDateTime(change.changedAt) : '—'}
            </TableCell>
            {withDelta && (
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                {delta(change)}
              </TableCell>
            )}
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {formatUnits(change.totalUnitsBefore)} → {formatUnits(change.totalUnitsAfter)}
            </TableCell>
            <TableCell>{change.changedBy || '—'}</TableCell>
            <TableCell>
              <Typography variant="caption">{change.note || '—'}</Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Historie celku jednotek fondu: každý pohyb (dávka, odkup, success fee, ruční oprava,
 * start modulu) s tím, kdo ho udělal a z čeho na co se celek změnil. V kartě posledních
 * pár pohybů, celá historie v dialogu.
 */
export function TotalUnitsHistory({ changes, error = null }: TotalUnitsHistoryProps) {
  const [open, setOpen] = useState(false)

  if (error) {
    return (
      <Typography variant="caption" color="error">
        {error}
      </Typography>
    )
  }
  if (changes.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        Zatím žádný pohyb celku jednotek.
      </Typography>
    )
  }

  return (
    <Box>
      <HistoryTable rows={changes.slice(0, MAX_ROWS)} />
      {changes.length > MAX_ROWS && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Zobrazeno {MAX_ROWS} z {changes.length} pohybů (nejnovější první).
          </Typography>
          <Button size="small" onClick={() => setOpen(true)}>
            Celá historie
          </Button>
        </Box>
      )}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Historie celku jednotek ({changes.length} pohybů)</DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <HistoryTable rows={changes} withDelta />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Zavřít</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
